/**
 * Golf Club Fitting Engine
 * 
 * Maps user profile characteristics to optimal club specifications
 * Produces fitter-quality recommendations based on real data
 */

import { UserProfile } from '../types';
import { 
  DRIVER_SPECS, 
  FAIRWAY_WOOD_SPECS, 
  HYBRID_SPECS, 
  IRON_SET_SPECS, 
  WEDGE_SPECS,
  ClubSpecs 
} from '../data/clubSpecsDatabase';
import { 
  DRIVER_SHAFTS, 
  IRON_SHAFTS, 
  ShaftSpecs,
  getRecommendedFlex,
  getRecommendedShaftWeight
} from '../data/shaftDatabase';

export interface FittingResult {
  club: ClubSpecs;
  shaft: ShaftSpecs | null;
  matchScore: number;
  matchReasons: string[];
  recommendedLoft: number | null;
  recommendedFlex: string;
  recommendedShaftWeight: number;
  adjustmentRecommendations: {
    loftSleeve?: string;
    faceAngle?: string;
    weight?: string;
    lie?: string;
  };
}

export interface FittingProfile {
  swingSpeed: number;
  handicap: number;
  ballFlight: 'slice' | 'fade' | 'straight' | 'draw' | 'hook';
  tempo: 'slow' | 'medium' | 'fast';
  height: number; // inches
  attackAngle?: 'steep' | 'neutral' | 'shallow';
  priority: 'distance' | 'accuracy' | 'forgiveness' | 'workability';
  budget: 'budget' | 'mid' | 'premium' | 'no-limit';
  issues?: string[];
}

/**
 * Convert UserProfile to FittingProfile
 */
export function profileToFitting(profile: UserProfile): FittingProfile {
  // Estimate swing speed if not provided
  const estimatedSwingSpeed = profile.swingSpeed || estimateSwingSpeed(profile);
  
  // Determine priority from issues
  let priority: FittingProfile['priority'] = 'forgiveness';
  if (profile.currentIssues?.includes('distance')) priority = 'distance';
  else if (profile.currentIssues?.includes('accuracy')) priority = 'accuracy';
  else if (profile.currentIssues?.includes('shot shaping')) priority = 'workability';
  else if (profile.handicap && profile.handicap < 10) priority = 'workability';
  
  // Map equipmentTier to fitting budget format
  const budgetMap: Record<string, FittingProfile['budget']> = {
    'budget': 'budget',
    'medium': 'mid',
    'premium': 'premium'
  };
  const userBudget = profile.equipmentTier ? budgetMap[profile.equipmentTier] : 'mid';
  
  return {
    swingSpeed: estimatedSwingSpeed,
    handicap: profile.handicap || 18,
    ballFlight: (profile.ballFlight as FittingProfile['ballFlight']) || 'straight',
    tempo: profile.swingTempo || 'medium',
    height: profile.height || 70,
    attackAngle: profile.attackAngle as FittingProfile['attackAngle'],
    priority,
    budget: userBudget,
    issues: profile.currentIssues || []
  };
}

/**
 * Estimate swing speed based on profile - uses averageScore for best accuracy
 */
function estimateSwingSpeed(profile: UserProfile): number {
  // BEST METHOD: Use average score if available (most accurate predictor)
  if (profile.averageScore) {
    let baseSpeed: number;
    const score = profile.averageScore;
    
    // Map score ranges to swing speeds based on golf persona research
    if (score >= 120) baseSpeed = 75;       // Absolute beginner
    else if (score >= 100) baseSpeed = 85;  // Weekend warrior
    else if (score >= 90) baseSpeed = 90;   // Committed improver
    else if (score >= 85) baseSpeed = 95;   // Bogey golfer
    else if (score >= 82) baseSpeed = 97;   // Mid handicapper
    else if (score >= 76) baseSpeed = 100;  // Low handicapper
    else if (score >= 71) baseSpeed = 105;  // Plus handicapper
    else baseSpeed = 108;                    // Scratch/Pro
    
    // Age adjustment
    const age = profile.age || 40;
    if (age < 30) baseSpeed += 5;
    else if (age > 50) baseSpeed -= 5;
    else if (age > 60) baseSpeed -= 10;
    else if (age > 70) baseSpeed -= 15;
    
    return Math.max(65, Math.min(120, baseSpeed));
  }
  
  // FALLBACK: Use handicap and other factors
  let baseSpeed = 95; // average male golfer
  
  // Adjust for age
  const age = profile.age || 40;
  if (age < 30) baseSpeed += 5;
  else if (age > 50) baseSpeed -= 5;
  else if (age > 60) baseSpeed -= 10;
  else if (age > 70) baseSpeed -= 15;
  
  // Adjust for skill level
  if (profile.skillLevel === 'advanced' || profile.playingExperience === 'serious') baseSpeed += 5;
  if (profile.skillLevel === 'beginner') baseSpeed -= 10;
  
  // Adjust for handicap (most important fallback factor)
  if (profile.handicap) {
    if (profile.handicap < 5) baseSpeed += 10;
    else if (profile.handicap < 10) baseSpeed += 5;
    else if (profile.handicap > 20) baseSpeed -= 5;
    else if (profile.handicap > 30) baseSpeed -= 10;
  }
  
  return Math.max(60, Math.min(130, baseSpeed));
}

/**
 * Get optimal loft based on swing characteristics
 */
export function getOptimalDriverLoft(swingSpeed: number, attackAngle?: string): number {
  // Base loft recommendations by swing speed
  let baseLoft: number;
  
  if (swingSpeed >= 115) baseLoft = 8.5;
  else if (swingSpeed >= 105) baseLoft = 9;
  else if (swingSpeed >= 95) baseLoft = 10.5;
  else if (swingSpeed >= 85) baseLoft = 11;
  else baseLoft = 12;
  
  // Adjust for attack angle
  if (attackAngle === 'steep') baseLoft += 1; // Need more loft to optimize launch
  if (attackAngle === 'shallow') baseLoft -= 0.5; // Can use less loft
  
  return baseLoft;
}

/**
 * Score a club against a fitting profile
 */
function scoreClubMatch(club: ClubSpecs, profile: FittingProfile): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  
  // SWING SPEED MATCHING (most important - up to 30 points)
  const [minSpeed, maxSpeed] = parseSwingSpeedRange(club.swingSpeedRange);
  if (profile.swingSpeed >= minSpeed && profile.swingSpeed <= maxSpeed) {
    score += 30;
    reasons.push(`Optimized for your ${profile.swingSpeed} mph swing speed`);
  } else if (Math.abs(profile.swingSpeed - minSpeed) <= 10 || Math.abs(profile.swingSpeed - maxSpeed) <= 10) {
    score += 15;
    reasons.push('Swing speed within acceptable range');
  }
  
  // HANDICAP MATCHING (up to 25 points)
  const [minHcp, maxHcp] = parseHandicapRange(club.handicapRange);
  if (profile.handicap >= minHcp && profile.handicap <= maxHcp) {
    score += 25;
    reasons.push(`Designed for ${profile.handicap} handicap golfers`);
  } else if (Math.abs(profile.handicap - minHcp) <= 5 || Math.abs(profile.handicap - maxHcp) <= 5) {
    score += 12;
  }
  
  // BALL FLIGHT CORRECTION (up to 20 points)
  if (profile.ballFlight === 'slice' || profile.ballFlight === 'fade') {
    if (club.bias === 'draw' || club.bias === 'slight-draw') {
      score += 20;
      reasons.push(`Draw bias helps correct your ${profile.ballFlight}`);
    } else if (club.bias === 'neutral') {
      score += 10;
    }
  } else if (profile.ballFlight === 'hook' || profile.ballFlight === 'draw') {
    if (club.bias === 'fade' || club.bias === 'slight-fade') {
      score += 20;
      reasons.push(`Fade bias helps correct your ${profile.ballFlight}`);
    } else if (club.bias === 'neutral') {
      score += 10;
    }
  } else if (profile.ballFlight === 'straight') {
    if (club.bias === 'neutral') {
      score += 15;
      reasons.push('Neutral bias maintains your straight ball flight');
    }
  }
  
  // PRIORITY MATCHING (up to 15 points)
  switch (profile.priority) {
    case 'distance':
      if (club.distanceRating && club.distanceRating >= 8) {
        score += 15;
        reasons.push('Maximizes distance');
      }
      if (club.spin === 'low' || club.spin === 'mid-low') score += 5;
      break;
    case 'forgiveness':
      if (club.forgiveness >= 8) {
        score += 15;
        reasons.push(`High forgiveness (${club.forgiveness}/10)`);
      }
      break;
    case 'accuracy':
      if (club.forgiveness >= 7 && club.workability >= 6) {
        score += 15;
        reasons.push('Balances forgiveness and control');
      }
      break;
    case 'workability':
      if (club.workability >= 8) {
        score += 15;
        reasons.push(`High workability for shot shaping (${club.workability}/10)`);
      }
      break;
  }
  
  // LAUNCH PROFILE MATCHING (up to 10 points)
  if (profile.swingSpeed < 90) {
    if (club.launch === 'high' || club.launch === 'mid-high') {
      score += 10;
      reasons.push('High launch optimizes carry for your swing speed');
    }
  } else if (profile.swingSpeed > 105) {
    if (club.launch === 'mid-low' || club.launch === 'mid') {
      score += 10;
      reasons.push('Mid launch prevents ballooning at high speeds');
    }
  }
  
  // CURRENT MODEL BONUS
  if (club.isCurrentModel) {
    score += 5;
    reasons.push('Current model with latest technology');
  }
  
  // TECHNOLOGY HIGHLIGHTS
  if (club.keyTechnologies && club.keyTechnologies.length > 0) {
    reasons.push(`Features: ${club.keyTechnologies.slice(0, 2).join(', ')}`);
  }
  
  return { score, reasons };
}

/**
 * Find best matching clubs for a category
 */
export function findBestClubs(
  category: ClubSpecs['category'],
  profile: FittingProfile,
  limit: number = 5
): FittingResult[] {
  let clubs: ClubSpecs[] = [];
  let shafts: ShaftSpecs[] = [];
  
  // Get appropriate clubs and shafts
  switch (category) {
    case 'driver':
      clubs = DRIVER_SPECS;
      shafts = DRIVER_SHAFTS;
      break;
    case 'fairway-wood':
      clubs = FAIRWAY_WOOD_SPECS;
      shafts = DRIVER_SHAFTS; // Fairway woods use driver-type shafts
      break;
    case 'hybrid':
      clubs = HYBRID_SPECS;
      shafts = DRIVER_SHAFTS;
      break;
    case 'iron-set':
      clubs = IRON_SET_SPECS;
      shafts = IRON_SHAFTS;
      break;
    case 'wedge':
      clubs = WEDGE_SPECS;
      shafts = IRON_SHAFTS;
      break;
  }
  
  // Filter clubs by budget tier - HARD FILTER, no fallback
  // EXACT MARKET PRICING BY CLUB TYPE (user-specified tiers)
  const filteredClubs = clubs.filter(club => {
    if (profile.budget === 'no-limit') return true;
    
    // Parse price from priceRange (e.g., "$400-500" -> 400)
    const priceMatch = club.priceRange?.match(/\$?([\d,]+)/);
    const price = priceMatch ? parseInt(priceMatch[1].replace(',', '')) : 500;
    
    // CATEGORY-SPECIFIC BUDGET TIERS
    if (category === 'driver') {
      // Drivers: Value $100-200, Mid $200-300, Premium $300+
      if (profile.budget === 'budget') return price >= 100 && price <= 200;
      if (profile.budget === 'mid') return price > 200 && price <= 300;
      if (profile.budget === 'premium') return price > 300;
    } else if (category === 'iron-set') {
      // Iron sets (full set): Value $500-700, Mid $700-1100, Premium $1100+
      if (profile.budget === 'budget') return price >= 500 && price <= 700;
      if (profile.budget === 'mid') return price > 700 && price <= 1100;
      if (profile.budget === 'premium') return price > 1100;
    } else if (category === 'wedge') {
      // Wedges (per club): Value $50-75, Mid $75-105, Premium $105+
      if (profile.budget === 'budget') return price >= 50 && price <= 75;
      if (profile.budget === 'mid') return price > 75 && price <= 105;
      if (profile.budget === 'premium') return price > 105;
    } else {
      // Fairway woods and hybrids: Value $100-180, Mid $180-250, Premium $250+
      if (profile.budget === 'budget') return price >= 100 && price <= 180;
      if (profile.budget === 'mid') return price > 180 && price <= 250;
      if (profile.budget === 'premium') return price > 250;
    }
    return true;
  });
  
  // HARD FILTER - use filtered clubs only, never fall back to all clubs
  // Better to show fewer accurate results than misleading ones
  const clubsToScore = filteredClubs;
  
  // Score all clubs
  const scoredClubs = clubsToScore.map(club => {
    const { score, reasons } = scoreClubMatch(club, profile);
    return { club, score, reasons };
  });
  
  // Sort by score and take top matches
  const topClubs = scoredClubs
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
  
  // Match with appropriate shafts
  return topClubs.map(({ club, score, reasons }) => {
    const shaftType = category === 'iron-set' || category === 'wedge' ? 'iron' : 'driver';
    const bestShaft = findBestShaft(shaftType, profile, shafts);
    const recommendedFlex = getRecommendedFlex(profile.swingSpeed, shaftType);
    const recommendedWeight = getRecommendedShaftWeight(profile.swingSpeed, profile.tempo, shaftType);
    
    // Determine recommended loft
    let recommendedLoft: number | null = null;
    if (category === 'driver' && club.lofts) {
      const optimalLoft = getOptimalDriverLoft(profile.swingSpeed, profile.attackAngle);
      recommendedLoft = club.lofts.reduce((prev, curr) => 
        Math.abs(curr - optimalLoft) < Math.abs(prev - optimalLoft) ? curr : prev
      );
    }
    
    // Generate adjustment recommendations
    const adjustments = generateAdjustmentRecommendations(club, profile);
    
    return {
      club,
      shaft: bestShaft,
      matchScore: score,
      matchReasons: reasons,
      recommendedLoft,
      recommendedFlex,
      recommendedShaftWeight: recommendedWeight,
      adjustmentRecommendations: adjustments
    };
  });
}

/**
 * Find best matching shaft
 */
function findBestShaft(type: 'driver' | 'iron', profile: FittingProfile, shafts: ShaftSpecs[]): ShaftSpecs | null {
  if (!shafts || shafts.length === 0) return null;
  
  const scoredShafts = shafts
    .filter(shaft => {
      // Filter by swing speed compatibility
      return profile.swingSpeed >= shaft.swingSpeedRange.min && 
             profile.swingSpeed <= shaft.swingSpeedRange.max;
    })
    .map(shaft => {
      let score = 0;
      
      // Tempo matching
      if (profile.tempo === 'slow' && (shaft.feel === 'soft' || shaft.feel === 'smooth')) score += 10;
      if (profile.tempo === 'medium' && shaft.feel === 'medium') score += 10;
      if (profile.tempo === 'fast' && (shaft.feel === 'firm' || shaft.feel === 'boardy')) score += 10;
      
      // Ball flight needs
      if ((profile.ballFlight === 'slice' || profile.ballFlight === 'fade') && 
          (shaft.launch === 'high' || shaft.launch === 'mid-high')) score += 8;
      if (profile.ballFlight === 'straight' && shaft.launch === 'mid') score += 8;
      if ((profile.ballFlight === 'hook' || profile.ballFlight === 'draw') && 
          (shaft.spin === 'low' || shaft.spin === 'mid-low')) score += 8;
      
      // Priority matching
      if (profile.priority === 'distance' && shaft.spin === 'low') score += 8;
      if (profile.priority === 'forgiveness' && shaft.launch === 'high') score += 8;
      if (profile.priority === 'accuracy' && shaft.stability >= 8) score += 8;
      
      // Handicap matching
      if (profile.handicap <= 5 && shaft.playerType === 'tour') score += 5;
      if (profile.handicap > 5 && profile.handicap <= 15 && shaft.playerType === 'better-player') score += 5;
      if (profile.handicap > 15 && shaft.playerType === 'mid-handicap') score += 5;
      if (profile.handicap > 25 && shaft.playerType === 'high-handicap') score += 5;
      
      // Current model bonus
      if (shaft.isCurrentModel) score += 3;
      
      return { shaft, score };
    });
  
  scoredShafts.sort((a, b) => b.score - a.score);
  return scoredShafts.length > 0 ? scoredShafts[0].shaft : null;
}

/**
 * Generate adjustment recommendations based on profile
 */
function generateAdjustmentRecommendations(club: ClubSpecs, profile: FittingProfile): FittingResult['adjustmentRecommendations'] {
  const adjustments: FittingResult['adjustmentRecommendations'] = {};
  
  // Loft sleeve recommendations
  if (profile.swingSpeed < 90) {
    adjustments.loftSleeve = '+1° to +2° for optimal launch';
  } else if (profile.swingSpeed > 110) {
    adjustments.loftSleeve = '-1° to reduce spin and lower flight';
  }
  
  // Face angle recommendations
  if (profile.ballFlight === 'slice') {
    adjustments.faceAngle = 'Close face 1-2° to help square at impact';
  } else if (profile.ballFlight === 'hook') {
    adjustments.faceAngle = 'Open face 0.5-1° to reduce hook tendency';
  }
  
  // Weight recommendations
  if (profile.ballFlight === 'slice' || profile.ballFlight === 'fade') {
    adjustments.weight = 'Move weight to heel for draw bias';
  } else if (profile.ballFlight === 'hook' || profile.ballFlight === 'draw') {
    adjustments.weight = 'Move weight to toe for fade bias';
  }
  
  // Lie angle recommendations based on height
  if (profile.height < 66) {
    adjustments.lie = '1-2° flat for your height';
  } else if (profile.height > 74) {
    adjustments.lie = '1-2° upright for your height';
  }
  
  return adjustments;
}

/**
 * Helper: Parse swing speed range string
 */
function parseSwingSpeedRange(range: string): [number, number] {
  if (range === 'all') return [0, 200];
  const match = range.match(/(\d+)-(\d+)/);
  if (match) {
    return [parseInt(match[1]), parseInt(match[2])];
  }
  return [80, 110]; // default
}

/**
 * Helper: Parse handicap range string
 */
function parseHandicapRange(range: string): [number, number] {
  if (range.includes('+')) {
    const match = range.match(/(\d+)\+/);
    return match ? [parseInt(match[1]), 54] : [15, 54];
  }
  const match = range.match(/(\d+)-(\d+)/);
  if (match) {
    return [parseInt(match[1]), parseInt(match[2])];
  }
  return [0, 54]; // default
}

/**
 * Generate complete fitting recommendation for all categories
 */
export function generateCompleteFitting(profile: FittingProfile): {
  driver: FittingResult[];
  fairwayWood: FittingResult[];
  hybrid: FittingResult[];
  irons: FittingResult[];
  wedges: FittingResult[];
} {
  return {
    driver: findBestClubs('driver', profile, 3),
    fairwayWood: findBestClubs('fairway-wood', profile, 3),
    hybrid: findBestClubs('hybrid', profile, 3),
    irons: findBestClubs('iron-set', profile, 3),
    wedges: findBestClubs('wedge', profile, 3)
  };
}

/**
 * Format fitting result for display
 */
export function formatFittingRecommendation(result: FittingResult): string {
  const lines: string[] = [];
  
  lines.push(`**${result.club.brand} ${result.club.model}** — ${result.matchScore}% Match`);
  
  if (result.recommendedLoft) {
    lines.push(`• Loft: ${result.recommendedLoft}°`);
  }
  
  if (result.shaft) {
    lines.push(`• Shaft: ${result.shaft.brand} ${result.shaft.model} ${result.recommendedShaftWeight}g ${result.recommendedFlex}`);
  }
  
  if (result.club.priceRange) {
    lines.push(`• Price: ${result.club.priceRange}`);
  }
  
  lines.push('');
  lines.push('**Why this club:**');
  result.matchReasons.slice(0, 4).forEach(reason => {
    lines.push(`• ${reason}`);
  });
  
  const adjustmentKeys = Object.keys(result.adjustmentRecommendations);
  if (adjustmentKeys.length > 0) {
    lines.push('');
    lines.push('**Recommended adjustments:**');
    if (result.adjustmentRecommendations.loftSleeve) {
      lines.push(`• Loft: ${result.adjustmentRecommendations.loftSleeve}`);
    }
    if (result.adjustmentRecommendations.faceAngle) {
      lines.push(`• Face: ${result.adjustmentRecommendations.faceAngle}`);
    }
    if (result.adjustmentRecommendations.weight) {
      lines.push(`• Weight: ${result.adjustmentRecommendations.weight}`);
    }
    if (result.adjustmentRecommendations.lie) {
      lines.push(`• Lie: ${result.adjustmentRecommendations.lie}`);
    }
  }
  
  return lines.join('\n');
}

/**
 * Generate concise recommendation summary for AI prompt
 */
export function generateFittingSummaryForAI(results: FittingResult[], category: string): string {
  if (results.length === 0) return '';
  
  const lines: string[] = [];
  lines.push(`TOP ${category.toUpperCase()} MATCHES FROM OUR DATABASE:`);
  
  results.forEach((result, index) => {
    const shaftInfo = result.shaft 
      ? ` with ${result.shaft.brand} ${result.shaft.model} ${result.recommendedShaftWeight}g ${result.recommendedFlex}` 
      : '';
    const loftInfo = result.recommendedLoft ? ` (${result.recommendedLoft}°)` : '';
    
    lines.push(`${index + 1}. ${result.club.brand} ${result.club.model}${loftInfo}${shaftInfo}`);
    lines.push(`   Match: ${result.matchScore}% | Launch: ${result.club.launch} | Spin: ${result.club.spin} | Forgiveness: ${result.club.forgiveness}/10`);
    lines.push(`   Best for: ${result.club.bestFor.slice(0, 3).join(', ')}`);
    if (result.matchReasons.length > 0) {
      lines.push(`   Why: ${result.matchReasons[0]}`);
    }
  });
  
  return lines.join('\n');
}

